import React, { useState, useEffect } from 'react';
import { Check, AlertTriangle } from 'lucide-react';

interface TermsOfServiceProps {
  onAccept: () => void;
  onDecline?: () => void;
}

export function TermsOfService({ onAccept, onDecline }: TermsOfServiceProps) {
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    console.log('🔵 TermsOfService component mounted');
    console.log('🔵 localStorage whipt_terms_accepted:', localStorage.getItem('whipt_terms_accepted'));
    return () => console.log('🔴 TermsOfService component unmounted');
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 50;
    if (isAtBottom && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 10000,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '800px',
          height: '90vh',
          backgroundColor: '#1e293b',
          borderRadius: '24px',
          border: '2px solid rgba(251, 191, 36, 0.3)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(to right, #f59e0b, #f97316)',
            padding: '24px',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: 'white',
              margin: '0 0 8px 0',
            }}
          >
            WHIPT Terms of Service
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0,
            }}
          >
            Please read carefully before using WHIPT
          </p>
          {!hasScrolled && (
            <div
              style={{
                marginTop: '16px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '12px',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  margin: 0,
                }}
              >
                📜 Scroll down to read all terms
              </p>
            </div>
          )}
        </div>

        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
          <div
            onScroll={handleScroll}
            style={{
              height: 'calc(90vh - 250px)',
              overflowY: 'scroll',
              overflowX: 'hidden',
              padding: '32px',
              backgroundColor: '#0f172a',
              color: '#cbd5e1',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                gap: '12px',
              }}
            >
              <AlertTriangle style={{ color: '#f87171', width: '24px', height: '24px', flexShrink: 0 }} />
              <div>
                <h3 style={{ color: '#fca5a5', fontWeight: 'bold', margin: '0 0 8px 0' }}>Important Notice</h3>
                <p style={{ color: '#fecaca', fontSize: '14px', margin: 0 }}>
                  WHIPT is an academic organization tool. It is NOT intended for emergency communications,
                  healthcare advice, or any life-critical situations.
                </p>
              </div>
            </div>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                1. Acceptance of Terms
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                By accessing and using WHIPT ("Where'd I Put That?"), you agree to be bound by these Terms of Service.
                If you do not agree to these terms, please do not use this application.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                2. Account Responsibilities
              </h3>
              <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px' }}>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must provide accurate and complete information during registration</li>
                <li>You must be at least 13 years old to use WHIPT</li>
                <li>One account per person - no sharing accounts</li>
                <li>Notify us immediately of any unauthorized access to your account</li>
              </ul>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                3. Community Guidelines & Chat Rules
              </h3>
              <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                <p style={{ fontWeight: '600', color: 'white', marginBottom: '8px' }}>Be Respectful & Professional:</p>
                <ul style={{ paddingLeft: '32px', lineHeight: '1.8' }}>
                  <li>Treat all classmates with respect and kindness</li>
                  <li>No harassment, bullying, hate speech, or discriminatory language</li>
                  <li>Respect diverse opinions and backgrounds</li>
                  <li>Keep academic discussions constructive and professional</li>
                </ul>

                <p style={{ fontWeight: '600', color: 'white', marginTop: '16px', marginBottom: '8px' }}>Academic Integrity:</p>
                <ul style={{ paddingLeft: '32px', lineHeight: '1.8' }}>
                  <li>Do not share assignment answers or test solutions</li>
                  <li>Help others learn, don't do their work for them</li>
                  <li>Follow your school's academic honesty policies</li>
                  <li>Cite sources and give credit where due</li>
                </ul>

                <p style={{ fontWeight: '600', color: 'white', marginTop: '16px', marginBottom: '8px' }}>Keep It Safe & Legal:</p>
                <ul style={{ paddingLeft: '32px', lineHeight: '1.8' }}>
                  <li>No inappropriate, explicit, or NSFW content</li>
                  <li>No sharing personal information (addresses, phone numbers, etc.)</li>
                  <li>No illegal activities, threats, or violence</li>
                  <li>No spam, advertising, or self-promotion</li>
                </ul>
              </div>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                4. Privacy & Data
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                We collect and store your academic data (assignments, schedules, notes) to provide the service.
                We do not sell your data. We may share anonymized usage statistics for research purposes.
                Review our Privacy Policy for full details.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                5. Virtual Rooms & Meetings
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                Virtual study rooms use WebRTC for peer-to-peer connections. Audio/video is not recorded by us,
                but participants may record. Behavior in virtual rooms must follow community guidelines.
                Moderators may remove disruptive users.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                6. Prohibited Activities
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
                You may not:
              </p>
              <ul style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px' }}>
                <li>Attempt to hack, break, or circumvent security</li>
                <li>Use bots, scripts, or automation without permission</li>
                <li>Impersonate others or create fake accounts</li>
                <li>Scrape or mass-download data</li>
                <li>Interfere with other users' access to the service</li>
              </ul>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                7. Consequences of Violations
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                Violations may result in: warnings, temporary suspension, permanent account termination,
                or reporting to school administrators. We reserve the right to remove content and
                terminate accounts at our discretion.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                8. Limitation of Liability
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                WHIPT is provided "as is" without warranties. We are not liable for any data loss,
                missed deadlines, technical issues, or any damages arising from use of this application.
                You are responsible for backing up your own work and meeting your own deadlines.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                9. Changes to Terms
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                We reserve the right to modify these terms at any time. Continued use of WHIPT
                after changes constitutes acceptance of new terms.
              </p>
            </section>

            <section style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                10. Contact & Reporting
              </h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
                To report violations, technical issues, or ask questions about these terms,
                please contact administrators through the app settings or email support.
              </p>
            </section>
          </div>
        </div>

        <div
          style={{
            padding: '20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: '#0b1224',
            display: 'flex',
            gap: '12px',
          }}
        >
          {onDecline && (
            <button
              onClick={onDecline}
              style={{
                flex: 1,
                padding: '12px 24px',
                borderRadius: '12px',
                backgroundColor: '#1e293b',
                color: 'white',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#334155')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#1e293b')}
            >
              Decline
            </button>
          )}

          <button
            onClick={hasScrolled ? onAccept : undefined}
            disabled={!hasScrolled}
            style={{
              flex: 1,
              padding: '12px 24px',
              borderRadius: '12px',
              background: hasScrolled ? 'linear-gradient(to right, #f59e0b, #f97316)' : '#475569',
              color: 'white',
              border: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: hasScrolled ? 'pointer' : 'not-allowed',
              opacity: hasScrolled ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              if (hasScrolled) {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(251, 191, 36, 0.5)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Check style={{ width: '20px', height: '20px' }} />
            <span>I Accept</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function hasAcceptedTerms(): boolean {
  return localStorage.getItem('whipt_terms_accepted') === 'true';
}

export function acceptTerms(): void {
  localStorage.setItem('whipt_terms_accepted', 'true');
  localStorage.setItem('whipt_terms_accepted_date', new Date().toISOString());
}
